/**
 * TRN (Tax Registration Number) validation utilities
 * TRN is a 9-digit number used in Jamaica for tax identification
 */

/** Common invalid TRN patterns */
const INVALID_TRNS = ['000000000', '123456789', '987654321'];

/** Check if TRN is all repeated digits (e.g., 111111111) */
const isRepeatedDigits = (trn: string): boolean => /^(\d)\1{8}$/.test(trn);

export interface TrnValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates a TRN (Tax Registration Number)
 * - Must be exactly 9 digits
 * - Cannot be common invalid patterns
 * - Cannot be all repeated digits
 */
export const validateTrn = (trn: string): TrnValidationResult => {
  // Remove any non-digit characters
  const digits = trn.replace(/\D/g, '');

  if (digits.length !== 9) {
    return { valid: false, error: 'TRN must be exactly 9 digits' };
  }

  if (INVALID_TRNS.includes(digits)) {
    return { valid: false, error: 'Please enter a valid TRN' };
  }

  if (isRepeatedDigits(digits)) {
    return { valid: false, error: 'TRN cannot be repeated digits' };
  }

  return { valid: true };
};

/**
 * Formats a TRN input value as XXX-XXX-XXX
 * Strips non-digits and applies formatting
 */
export const formatTrn = (value: string): string => {
  // Remove all non-digit characters and limit to 9 digits
  const digits = value.replace(/\D/g, '').slice(0, 9);

  // Apply XXX-XXX-XXX formatting
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
